import { IonApp, IonContent, IonPage, IonSpinner } from "@ionic/react";
import RevenueTable from "components/mobile-components/Accounts/RevenueTable";
import BackButtonTitle from "components/mobile-components/Common/BackButtonTitle";
import { COUNTRYINFO } from "data/country";
import { dateTimeFormatter } from "helpers/helper";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Bar, getElementAtEvent } from "react-chartjs-2";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getSelectedDate } from "redux/container/dashboardSlice";
import Accounts from "services/Accounts";

const TotalRevenuePage = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const revenueBarRef = useRef();
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const dateInfo = useSelector((state) => state.dashboard.dateInfo);
  const createUserData = useSelector((state) => state.user.createUserData);
  const dateFormat = useSelector((state) => state.date.format);
  const ekkLanguage = useSelector((state) => state.lan.lan);
  const [filterClicked, setFilterClicked] = useState(false);
  const [selectLevel, setSelectLevel] = useState(dateInfo?.level || "today");
  const [selectedDate, setSelectedDate] = useState(
    dateInfo ? JSON.stringify(dateInfo) : moment().toISOString()
  );
  const [revenueData, setRevenueData] = useState([]);
  const [revenueLoader, setRevenueLoader] = useState(false);
  const [currency, setCurrency] = useState("$");

  const [revenueDateRange, setRevenueDateRange] = useState({
    start: searchParams.get("start")
      ? moment(searchParams.get("start")).format("YYYY-MM-DD")
      : moment().startOf("month").format("YYYY-MM-DD"),
    end: searchParams.get("end")
      ? moment(searchParams.get("end")).format("YYYY-MM-DD")
      : moment().endOf("month").format("YYYY-MM-DD"),
    value: "month",
  });

  const [revenueTableData, setRevenueTableData] = useState([]);
  const [loadingRevenueTable, setLoadingRevenueTable] = useState(false);
  const [totalRevenueAmount, setTotalRevenueAmount] = useState(0);
  const [showRevenueTable, setShowRevenueTable] = useState(false);

  const revenueChartData = {
    labels: revenueData?.map((item) =>
      moment(item?.month, "MM-YYYY").format("MMM/YY")
    ),
    datasets: [
      {
        data: revenueData?.map((item) => item?.total_amount),
        backgroundColor: "#00B224",
      },
    ],
  };

  const chartOptions = {
    barPercentage: 0.7,
    plugins: {
      legend: false,
    },
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      setRevenueLoader(true);
      const accountType = await Accounts.getAccountType("Revenue");

      if (accountType.success) {
        const response = await Accounts.getRevenueData(timeZone);
        if (response.status === 200) {
          setRevenueData(response.data);
        }
      }
      setRevenueLoader(false);
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchRevenueTable = async () => {
      setLoadingRevenueTable(true);
      const response = await Accounts.getRevenueDate(
        revenueDateRange.start,
        revenueDateRange.end
      );

      if (response.status === 200) {
        setRevenueTableData(response.results);
        setTotalRevenueAmount(response.total);
        setCurrency(response?.currency || "$");
      } else {
        setRevenueTableData([]);
        setTotalRevenueAmount(0);
      }
      setLoadingRevenueTable(false);
    };

    fetchRevenueTable();
  }, [revenueDateRange]);

  const barClickHandler = (event) => {
    const elementIndex = getElementAtEvent(revenueBarRef.current, event)?.[0]
      ?.index;
    if (elementIndex === undefined) return;

    const dateLabel = revenueChartData.labels[elementIndex];
    const value = revenueChartData.datasets[0].data[elementIndex];
    if (value === 0) return;

    const startTime = moment(dateLabel, "MMM/YY")
      .startOf("month")
      .format("YYYY-MM-DD");
    const endTime = moment(dateLabel, "MMM/YY")
      .endOf("month")
      .format("YYYY-MM-DD");

    const dateTime = { start: startTime, end: endTime, value: "month" };

    setRevenueDateRange({ start: startTime, end: endTime });
    setShowRevenueTable(true);
    dispatch(getSelectedDate(dateTime));
  };
  const handleCrossFilter = () => {
    let date = {
      value: "month",
      label: ekkLanguage.dashboard.thisMonth,
      name: "This Month",
    };
    let timeDate = dateTimeFormatter(date?.value);
    let dateTime = { ...timeDate, level: date?.name };
    setRevenueDateRange({
      start: moment(timeDate?.start).format("YYYY-MM-DD"),
      end: moment(timeDate?.end).format("YYYY-MM-DD"),
    });
    dispatch(getSelectedDate(dateTime));
    setFilterClicked(false);
  };

  return (
    <IonApp>
      <IonPage>
        <IonContent>
          <BackButtonTitle
            title="Revenue"
            filter={true}
            setGetDate={({ start, end }) =>
              setRevenueDateRange({
                start: moment(start).format("YYYY-MM-DD"),
                end: moment(end).format("YYYY-MM-DD"),
              })
            }
            setFilterClicked={setFilterClicked}
            filterClicked={filterClicked}
          />

          <div className="pt-14 px-4">
            {!filterClicked && (
              <div className="chart-wrapper relative mb-4">
                {revenueLoader && (
                  <div className="absolute h-full w-full flex justify-center items-center left-[7%] top-[5%]">
                    <IonSpinner name="circular" />
                  </div>
                )}

                <div className="px-2 pb-1 flex justify-between items-center pt-5">
                  <h4 className="font-bold text-18 sm:text-20 text-black-1000 mb-2">
                    Total Revenue
                  </h4>
                </div>

                <div
                  className="flex justify-center px-2"
                  style={{ width: "100%", maxWidth: "768px" }}
                >
                  <Bar
                    data={revenueChartData}
                    options={chartOptions}
                    ref={revenueBarRef}
                    onClick={(e) => barClickHandler(e)}
                  />
                </div>
              </div>
            )}

            <RevenueTable
              loading={loadingRevenueTable}
              revenueData={revenueTableData}
              totalAmount={totalRevenueAmount}
              getDate={revenueDateRange}
              dateFormat={dateFormat}
              currency={currency}
              notFoundText={ekkLanguage?.orderList?.notFound}
              filterClicked={filterClicked}
              handleCrossFilter={handleCrossFilter}
            />
          </div>
        </IonContent>
      </IonPage>
    </IonApp>
  );
};

export default TotalRevenuePage;
